import React, { useState, useEffect } from "react";
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  TextInput,
  Dropdown,
  InlineNotification,
  FormGroup,
  Checkbox,
  TextArea,
} from "@carbon/react";
import { useIntl } from "react-intl";
import {
  createConfiguration,
  updateConfiguration,
  getConfigurationByAnalyzerId,
} from "../../../services/fileImportService";
import { getAnalyzers } from "../../../services/analyzerService";
import "./FileImportConfiguration.css";

const FileImportConfiguration = ({
  configuration,
  open,
  onClose,
  preselectedAnalyzerId,
}) => {
  const intl = useIntl();

  const [formData, setFormData] = useState({
    analyzerId: null,
    fileFormat: "CSV",
    importDirectory: "",
    filePattern: "*.csv",
    archiveDirectory: "",
    errorDirectory: "",
    columnMappings: "{}",
    delimiter: ",",
    hasHeader: true,
    active: true,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [availableAnalyzers, setAvailableAnalyzers] = useState([]);
  const [autoLoadedConfig, setAutoLoadedConfig] = useState(null);

  // Use explicit configuration prop, or auto-loaded config from preselectedAnalyzerId
  const effectiveConfiguration = configuration || autoLoadedConfig;
  const isEditMode = !!effectiveConfiguration;

  // Load analyzers for dropdown, and auto-load existing config if preselectedAnalyzerId
  useEffect(() => {
    if (open) {
      getAnalyzers({}, (data) => {
        const list =
          data && Array.isArray(data.analyzers) ? data.analyzers : [];
        setAvailableAnalyzers(list);
      });

      // If we have a preselected analyzer and no explicit configuration,
      // try to load the existing file import config for this analyzer
      if (preselectedAnalyzerId && !configuration) {
        getConfigurationByAnalyzerId(preselectedAnalyzerId, (data) => {
          if (data && data.id && !data.error) {
            setAutoLoadedConfig(data);
          }
        });
      }
    } else {
      setAutoLoadedConfig(null);
    }
  }, [open, preselectedAnalyzerId, configuration]);

  // Initialize form data when configuration changes
  useEffect(() => {
    if (effectiveConfiguration) {
      setFormData({
        analyzerId: effectiveConfiguration.analyzerId,
        fileFormat: effectiveConfiguration.fileFormat || "CSV",
        importDirectory: effectiveConfiguration.importDirectory || "",
        filePattern: effectiveConfiguration.filePattern || "*.csv",
        archiveDirectory: effectiveConfiguration.archiveDirectory || "",
        errorDirectory: effectiveConfiguration.errorDirectory || "",
        columnMappings: effectiveConfiguration.columnMappings
          ? JSON.stringify(effectiveConfiguration.columnMappings, null, 2)
          : "{}",
        delimiter: effectiveConfiguration.delimiter || ",",
        hasHeader:
          effectiveConfiguration.hasHeader !== undefined
            ? effectiveConfiguration.hasHeader
            : true,
        active:
          effectiveConfiguration.active !== undefined
            ? effectiveConfiguration.active
            : true,
      });
    } else {
      setFormData({
        analyzerId: preselectedAnalyzerId || null,
        fileFormat: "CSV",
        importDirectory: "",
        filePattern: "*.csv",
        archiveDirectory: "",
        errorDirectory: "",
        columnMappings: "{}",
        delimiter: ",",
        hasHeader: true,
        active: true,
      });
    }
    setErrors({});
    setNotification(null);
  }, [effectiveConfiguration, open, preselectedAnalyzerId]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.analyzerId) {
      newErrors.analyzerId = intl.formatMessage({
        id: "file.import.configuration.validation.analyzerId.required",
      });
    }

    if (!formData.importDirectory.trim()) {
      newErrors.importDirectory = intl.formatMessage({
        id: "file.import.configuration.validation.importDirectory.required",
      });
    }

    // Validate column mappings JSON
    if (formData.columnMappings) {
      try {
        JSON.parse(formData.columnMappings);
      } catch (e) {
        newErrors.columnMappings = intl.formatMessage({
          id: "file.import.configuration.validation.columnMappings.invalid",
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setNotification(null);

    // Parse column mappings JSON
    let columnMappingsObj = {};
    try {
      columnMappingsObj = JSON.parse(formData.columnMappings);
    } catch (e) {
      setNotification({
        kind: "error",
        title: intl.formatMessage({
          id: "file.import.configuration.error.save",
        }),
        subtitle: intl.formatMessage({
          id: "file.import.configuration.validation.columnMappings.invalid",
        }),
      });
      setIsSubmitting(false);
      return;
    }

    const submitData = {
      analyzerId: formData.analyzerId,
      fileFormat: formData.fileFormat || "CSV",
      importDirectory: formData.importDirectory.trim(),
      filePattern: formData.filePattern || "*.csv",
      archiveDirectory: formData.archiveDirectory.trim() || null,
      errorDirectory: formData.errorDirectory.trim() || null,
      columnMappings: columnMappingsObj,
      delimiter: formData.delimiter || ",",
      hasHeader: formData.hasHeader,
      active: formData.active,
    };

    const callback = (response, extraParams) => {
      setIsSubmitting(false);
      if (response.error || response.statusCode >= 400) {
        setNotification({
          kind: "error",
          title: intl.formatMessage({
            id: "file.import.configuration.error.save",
          }),
          subtitle:
            response.error ||
            response.message ||
            intl.formatMessage({
              id: "file.import.configuration.error.unknown",
            }),
        });
      } else {
        setNotification({
          kind: "success",
          title: intl.formatMessage({
            id: isEditMode
              ? "file.import.configuration.success.updated"
              : "file.import.configuration.success.created",
          }),
        });
        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    };

    if (isEditMode) {
      updateConfiguration(effectiveConfiguration.id, submitData, callback);
    } else {
      createConfiguration(submitData, callback);
    }
  };

  // Analyzer options for dropdown
  const analyzerOptions = availableAnalyzers.map((analyzer) => ({
    id: analyzer.id,
    text: analyzer.name || `Analyzer ${analyzer.id}`,
  }));

  const fileFormatOptions = [
    {
      id: "CSV",
      text: intl.formatMessage({ id: "fileImport.format.csv" }),
    },
    {
      id: "TSV",
      text: intl.formatMessage({ id: "fileImport.format.tsv" }),
    },
    {
      id: "EXCEL",
      text: intl.formatMessage({ id: "fileImport.format.excel" }),
    },
  ];

  const hideDelimitedFileOptions = formData.fileFormat === "EXCEL";

  return (
    <ComposedModal
      open={open}
      onClose={onClose}
      data-testid="file-import-configuration-form"
      className="file-import-configuration-modal"
    >
      <ModalHeader
        title={intl.formatMessage({
          id: isEditMode
            ? "file.import.configuration.title.edit"
            : "file.import.configuration.title.add",
        })}
        data-testid="file-import-configuration-form-header"
      />
      <ModalBody>
        {notification && (
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onClose={() => setNotification(null)}
            data-testid="file-import-configuration-form-notification"
          />
        )}

        <FormGroup legendText="">
          <Dropdown
            id="file-import-analyzer"
            data-testid="file-import-configuration-analyzer-dropdown"
            titleText={intl.formatMessage({
              id: "file.import.configuration.analyzer",
            })}
            label={intl.formatMessage({
              id: "file.import.configuration.analyzer.placeholder",
            })}
            items={analyzerOptions}
            selectedItem={
              analyzerOptions.find((opt) => opt.id === formData.analyzerId) ||
              null
            }
            itemToString={(item) => (item ? item.text : "")}
            onChange={({ selectedItem }) =>
              handleFieldChange("analyzerId", selectedItem?.id || null)
            }
            invalid={!!errors.analyzerId}
            invalidText={errors.analyzerId}
            required
            disabled={isEditMode} // Don't allow changing analyzer in edit mode
          />
          {analyzerOptions.length === 0 && (
            <p
              className="file-import-configuration-helper"
              data-testid="file-import-configuration-empty-plugin-message"
            >
              {intl.formatMessage({
                id: "file.import.configuration.analyzer.emptyState",
              })}
            </p>
          )}

          <Dropdown
            id="file-import-format"
            data-testid="file-import-configuration-file-format-dropdown"
            titleText={intl.formatMessage({
              id: "file.import.configuration.fileFormat",
            })}
            label={intl.formatMessage({
              id: "file.import.configuration.fileFormat.placeholder",
            })}
            items={fileFormatOptions}
            selectedItem={
              fileFormatOptions.find((opt) => opt.id === formData.fileFormat) ||
              fileFormatOptions[0]
            }
            itemToString={(item) => (item ? item.text : "")}
            onChange={({ selectedItem }) =>
              handleFieldChange("fileFormat", selectedItem?.id || "CSV")
            }
            required
          />

          <TextInput
            id="file-import-directory"
            data-testid="file-import-configuration-directory-input"
            labelText={intl.formatMessage({
              id: "file.import.configuration.importDirectory",
            })}
            placeholder={intl.formatMessage({
              id: "file.import.configuration.importDirectory.placeholder",
            })}
            value={formData.importDirectory}
            onChange={(e) =>
              handleFieldChange("importDirectory", e.target.value)
            }
            invalid={!!errors.importDirectory}
            invalidText={errors.importDirectory}
            required
            helperText={intl.formatMessage({
              id: "file.import.configuration.importDirectory.helperText",
            })}
          />

          <TextInput
            id="file-import-pattern"
            data-testid="file-import-configuration-pattern-input"
            labelText={intl.formatMessage({
              id: "file.import.configuration.filePattern",
            })}
            placeholder="*.csv"
            value={formData.filePattern}
            onChange={(e) => handleFieldChange("filePattern", e.target.value)}
            helperText={intl.formatMessage({
              id: "file.import.configuration.filePattern.helperText",
            })}
          />

          <TextInput
            id="file-import-archive"
            data-testid="file-import-configuration-archive-input"
            labelText={intl.formatMessage({
              id: "file.import.configuration.archiveDirectory",
            })}
            placeholder={intl.formatMessage({
              id: "file.import.configuration.archiveDirectory.placeholder",
            })}
            value={formData.archiveDirectory}
            onChange={(e) =>
              handleFieldChange("archiveDirectory", e.target.value)
            }
            helperText={intl.formatMessage({
              id: "file.import.configuration.archiveDirectory.helperText",
            })}
          />

          <TextInput
            id="file-import-error"
            data-testid="file-import-configuration-error-input"
            labelText={intl.formatMessage({
              id: "file.import.configuration.errorDirectory",
            })}
            placeholder={intl.formatMessage({
              id: "file.import.configuration.errorDirectory.placeholder",
            })}
            value={formData.errorDirectory}
            onChange={(e) =>
              handleFieldChange("errorDirectory", e.target.value)
            }
            helperText={intl.formatMessage({
              id: "file.import.configuration.errorDirectory.helperText",
            })}
          />

          <TextArea
            id="file-import-column-mappings"
            data-testid="file-import-configuration-column-mappings-input"
            labelText={intl.formatMessage({
              id: "file.import.configuration.columnMappings",
            })}
            placeholder='{"Sample_ID": "sampleId", "Test_Code": "testCode", "Result": "result"}'
            value={formData.columnMappings}
            onChange={(e) =>
              handleFieldChange("columnMappings", e.target.value)
            }
            invalid={!!errors.columnMappings}
            invalidText={errors.columnMappings}
            rows={4}
            helperText={intl.formatMessage({
              id: "file.import.configuration.columnMappings.helperText",
            })}
          />

          {!hideDelimitedFileOptions && (
            <>
              <TextInput
                id="file-import-delimiter"
                data-testid="file-import-configuration-delimiter-input"
                labelText={intl.formatMessage({
                  id: "file.import.configuration.delimiter",
                })}
                placeholder=","
                value={formData.delimiter}
                onChange={(e) => handleFieldChange("delimiter", e.target.value)}
                helperText={intl.formatMessage({
                  id: "file.import.configuration.delimiter.helperText",
                })}
              />

              <Checkbox
                id="file-import-has-header"
                data-testid="file-import-configuration-has-header-checkbox"
                labelText={intl.formatMessage({
                  id: "file.import.configuration.hasHeader",
                })}
                checked={formData.hasHeader}
                onChange={(_, { checked }) =>
                  handleFieldChange("hasHeader", checked)
                }
              />
            </>
          )}

          <Checkbox
            id="file-import-active"
            data-testid="file-import-configuration-active-checkbox"
            labelText={intl.formatMessage({
              id: "file.import.configuration.active",
            })}
            checked={formData.active}
            onChange={(_, { checked }) => handleFieldChange("active", checked)}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          onClick={onClose}
          data-testid="file-import-configuration-form-cancel-button"
        >
          {intl.formatMessage({ id: "file.import.configuration.cancel" })}
        </Button>
        <Button
          kind="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-testid="file-import-configuration-form-save-button"
        >
          {intl.formatMessage({ id: "file.import.configuration.save" })}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default FileImportConfiguration;
